//React
import * as React from "react";
//Material UI
import Box from "@mui/material/Box";
//Routing
import { useNavigate } from "react-router-dom";
//Other
import tjommieIcon from "../../Global/images/tjommie-icon.svg";

/**
 * BusiOps launcher, built to NeuraDesigns `busiops-dashboard/index.html`.
 *
 * The design's seventh card is "Field Rollout — NMBM Tjommie", which does not
 * exist in this app; Residents Analytics occupies that slot instead and takes
 * its treatment.
 *
 * `kind` drives the badge and link wording, and is a real distinction here
 * rather than the mockup's: `internal` stays inside Userport, `external`
 * leaves for another origin. The design's two badge variants map onto exactly
 * that.
 */

/**
 * Builds an external target from its base URL.
 *
 * CRA inlines `process.env.REACT_APP_*` at build time, so an unset variable
 * compiles to the **literal string** "undefined" rather than to undefined —
 * which is how a card ends up linking to `/undefined`. An empty variable is
 * just as bad once a path is appended: `""` + "/analytics" is a same-origin
 * link to a route that does not exist.
 *
 * Returns null when the base is not configured, so the card renders without
 * an href instead of advertising a broken destination.
 */
function externalHref(base, path = "") {
  if (!base || base === "undefined" || base === "null") return null;
  return `${base}${path}`;
}

const APPS = [
  {
    title: "User Port",
    body: "Internal user management — add, edit or remove access to the Neura product suite, and control which product-area roles each person holds.",
    emoji: "👤",
    tint: "rgba(253, 185, 64, 0.16)",
    kind: "internal",
    to: "/userport",
  },
  {
    title: "Tjommie (SmartNode)",
    body: "Real-time SmartNode control — device switching, scheduling, load shifting.",
    emoji: "⚡",
    tint: "rgba(59, 130, 246, 0.14)",
    kind: "external",
    href: externalHref(process.env.REACT_APP_SMARTNODE_URL),
  },
  {
    title: "Inventory Management",
    body: "Stock tracking, JobCard lifecycle, installer assignments, warehouse ops.",
    emoji: "📦",
    tint: "rgba(99, 102, 241, 0.14)",
    kind: "external",
    href: externalHref(process.env.REACT_APP_IMS_URL),
  },
  {
    title: "Residents Analytics",
    body: "Engagement and usage metrics for the residents app, by device and suburb.",
    emoji: "📈",
    tint: "rgba(253, 185, 64, 0.16)",
    kind: "internal",
    to: "/residents-analytics",
  },
  {
    title: "Test Bench",
    body: "QA & pre-deployment testing environment for SmartNode firmware and integrations.",
    emoji: "🧪",
    tint: "rgba(20, 184, 166, 0.14)",
    kind: "external",
    href: externalHref(process.env.REACT_APP_TB_URL, "/"),
  },
  {
    title: "Reliability Monitoring Analytics",
    body: "Aggregated device connectivity and MQTT-broker reliability metrics.",
    emoji: "🛡️",
    tint: "rgba(239, 68, 68, 0.14)",
    kind: "external",
    href: externalHref(process.env.REACT_APP_SMARTNODE_URL, "/analytics"),
  },
];

function ArrowIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
      <path d="M5 12h14M12 5l7 7-7 7" />
    </svg>
  );
}

function AppCard({ app }) {
  const navigate = useNavigate();
  const isInternal = app.kind === "internal";
  const href = isInternal ? app.to : app.href;

  // Rendered as a real anchor, as the design does, so middle-click and
  // keyboard activation behave. Internal targets are intercepted so the
  // router handles them instead of a full page load. An external app with no
  // configured URL gets no href at all rather than a broken one.
  return (
    <Box
      component="a"
      href={href || undefined}
      aria-disabled={href ? undefined : "true"}
      onClick={
        isInternal
          ? (event) => {
              event.preventDefault();
              navigate(app.to);
            }
          : undefined
      }
      sx={{
        background: "var(--surface)",
        border: "1px solid var(--border)",
        borderRadius: "var(--radius-xl)",
        padding: "var(--space-6)",
        textDecoration: "none",
        color: "inherit",
        transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
        display: "flex",
        flexDirection: "column",
        gap: "var(--space-3)",
        "&:hover": {
          transform: "translateY(-3px)",
          boxShadow: "0 8px 32px rgba(253, 185, 64, 0.1)",
          borderColor: "rgba(253, 185, 64, 0.35)",
        },
        "&:hover .link-row svg": { transform: "translateX(3px)" },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "12px" }}>
        <Box
          aria-hidden="true"
          sx={{
            width: 40,
            height: 40,
            borderRadius: "10px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "18px",
            flexShrink: 0,
            background: app.tint,
          }}
        >
          {app.emoji}
        </Box>
        <Box
          component="span"
          sx={{
            fontSize: "0.7rem",
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            padding: "3px 8px",
            borderRadius: "4px",
            whiteSpace: "nowrap",
            background: isInternal ? "rgba(255, 255, 255, 0.06)" : "rgba(99, 102, 241, 0.16)",
            color: isInternal ? "rgba(255, 255, 255, 0.4)" : "#A5B4FC",
          }}
        >
          {isInternal ? "Internal" : "External"}
        </Box>
      </Box>

      <Box component="h3" sx={{ fontSize: "1.05rem", fontWeight: 600, color: "#fff", letterSpacing: "-0.01em" }}>
        {app.title}
      </Box>

      <Box
        component="p"
        sx={{ fontSize: "0.875rem", color: "rgba(255, 255, 255, 0.5)", lineHeight: 1.6, flexGrow: 1 }}
      >
        {app.body}
      </Box>

      <Box
        className="link-row"
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          fontSize: "0.8rem",
          color: "var(--accent)",
          fontWeight: 600,
          marginTop: "4px",
          "& svg": { transition: "transform 0.2s" },
        }}
      >
        {isInternal ? "Open" : "Launch"}
        <ArrowIcon />
      </Box>
    </Box>
  );
}

export default function DashboardPage() {
  return (
    <Box
      sx={{
        maxWidth: "var(--container-max)",
        padding: "var(--space-9) var(--container-pad-x) 80px",
        margin: "0 auto",
        "@media (max-width: 720px)": {
          padding: "var(--space-7) var(--space-4) 60px",
        },
      }}
    >
      <Box sx={{ marginBottom: "var(--space-8)" }}>
        <Box
          component="h1"
          sx={{
            fontSize: "2rem",
            fontWeight: 700,
            letterSpacing: "-0.025em",
            marginBottom: "var(--space-2)",
            display: "flex",
            alignItems: "center",
          }}
        >
          <Box
            component="img"
            src={tjommieIcon}
            alt=""
            sx={{ height: "0.78em", width: "auto", marginRight: "12px", flexShrink: 0 }}
          />
          Neura BusiOps
        </Box>
        <Box component="p" sx={{ fontSize: "1rem", color: "rgba(255, 255, 255, 0.5)", lineHeight: 1.6 }}>
          Internal portal — jump into Neura apps and manage users.
        </Box>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
          gap: "var(--space-5)",
          "@media (max-width: 720px)": { gridTemplateColumns: "1fr" },
        }}
      >
        {APPS.map((app) => (
          <AppCard key={app.title} app={app} />
        ))}
      </Box>
    </Box>
  );
}
